/**
 * La première proposition quand on arrive sur son profil (Tom, 2026-09-17) :
 * partager son lien EventMatch sur LinkedIn et en story, pour qu'on puisse
 * vous retrouver et prendre rendez-vous avec vous. Une phrase, trois gestes.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Download, Share2 } from 'lucide-react';
import { appelerApi } from '../../reseau.ts';
import type { Espace, MonParrainage } from './types.ts';

export function PartagerMonLien({ espace }: { espace: Espace }) {
  const pseudo = espace.profil?.pseudo ?? null;
  const [parrainage, setParrainage] = useState<MonParrainage | null>(null);
  const [copie, setCopie] = useState(false);

  useEffect(() => {
    if (!pseudo) return;
    void appelerApi<MonParrainage>('/api/parrainage/moi').then((r) => setParrainage(r.donnees));
  }, [pseudo]);

  const url = pseudo ? `${location.origin}/${pseudo}` : null;
  const texte = 'Retrouvez-moi sur EventMatch.pro et prenez rendez-vous avec moi : ';

  const copier = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(`${texte}${url}`).catch(() => undefined);
    setCopie(true);
    setTimeout(() => setCopie(false), 2000);
  };

  return (
    <div className="rounded-[10px] border border-graphite px-4 py-3">
      <div className="flex items-start gap-3">
        <Share2 size={16} strokeWidth={1.5} className="mt-0.5 shrink-0 text-fumee" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] text-blanc">
            Partagez sur votre profil LinkedIn et en story sur vos réseaux votre lien EventMatch.pro pour qu’on
            puisse vous retrouver et prendre rendez-vous avec vous !
          </p>
          {url ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
                target="_blank"
                rel="noreferrer"
                className="btn-creme !min-h-0 px-4 py-1.5 text-[13px]"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" className="mr-1.5 inline"><path fill="currentColor" d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7c.6-1 1.9-2 3.9-2 4.1 0 4.8 2.7 4.8 6.2V21h-4v-5.3c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V21h-4V9Z" /></svg>
                Partager sur LinkedIn
              </a>
              <button onClick={() => void copier()} className="rounded-full border border-graphite px-4 py-1.5 text-[13px] text-fumee hover:text-blanc">
                {copie ? <Check size={13} strokeWidth={1.5} className="mr-1.5 inline" /> : <Copy size={13} strokeWidth={1.5} className="mr-1.5 inline" />}
                {copie ? 'Copié' : 'Copier le lien'}
              </button>
              {parrainage?.qr_page && (
                <a href={parrainage.qr_page} download={`eventmatch-${pseudo}.png`} className="rounded-full border border-graphite px-4 py-1.5 text-[13px] text-fumee hover:text-blanc">
                  <Download size={13} strokeWidth={1.5} className="mr-1.5 inline" />
                  QR pour votre story
                </a>
              )}
              <span className="text-[12px] text-fumee">{url.replace(/^https?:\/\//, '')}</span>
            </div>
          ) : (
            <p className="mt-2 text-[13px] text-fumee">
              Choisissez d’abord votre adresse dans{' '}
              <Link to="/compte/entreprise#adresse" className="text-blanc underline">Moi et mon entreprise</Link> — par exemple eventmatch.pro/tomwart.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
